import { IPromise } from 'angular';
import { put } from 'redux-saga/effects';

import { Messages } from '../../../../codegen/Localize';
import { IMetricInfo } from '../../../../metrics/models/IMetricInfo';
import { Metrics } from '../../../../metrics/resources/Metrics';
import { PublishedConnections } from '../../../../publishedConnection/resources/PublishedConnections';
import { ListResult } from '../../../../ts/fetchers/ListResult';
import { Filter } from '../../../../ts/helpers/Filter';
import { FlowBasicInfo } from '../../../../ts/models/FlowInfo';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IDataConnectionInfo } from '../../../../ts/models/IDataConnectionInfo';
import { IDataRoleDetailInfo } from '../../../../ts/models/IDataRoleInfo';
import { IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import { IPublishedConnectionDetailInfo, isPublishedConnectionInfo } from '../../../../ts/models/IPublishedConnectionInfo';
import { IViewDetailInfo } from '../../../../ts/models/IViewInfo';
import { IWorkbookInfo } from '../../../../ts/models/IWorkbookInfo';
import { IExplorableItemTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { DataConnections } from '../../../../ts/resources/DataConnections';
import { DataRoles } from '../../../../ts/resources/Dataroles';
import { DataSources } from '../../../../content/resources/Datasources';
import { Flows } from '../../../../ts/resources/Flows';
import { Workbooks } from '../../../../content/resources/Workbooks';
import { ContentDraftStatus } from '../../../../ts/ServerApi/types/ContentDraft';
import { Views } from '../../../../views/resources/Views';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { GetContentDetailAction, updateContentDetail } from '../../../actions/spaces/GetContentDetailAction';
import { ServiceLocator } from '../../../ServiceLocator';

export function* processGetContentDetailAction(action: GetContentDetailAction) {
  try {
    switch(action.content.contentType){
      case 'project':
        break;
      case 'workbook':
        let workbookContent = null;
        if (!Workbooks.isWorkbookInfo(action.content)) {
          // getting workbook info instead of workbook detail info, because numSheets does not exist in workbook detail info
          workbookContent = getWorkbookInfo(action.content);
        }

        let datasourceList = null;
        if(!action.content.hasOwnProperty('datasources')){
          datasourceList = getDatasourcesForWorkbook(action.content.id);
        }

        let updatedContent: any = null;
        yield Promise.all([workbookContent, datasourceList]).then(function(results){
          if(results[0]){
            updatedContent = results[0];
          }
          if(results[1]){
            updatedContent = updatedContent || action.content;
            updatedContent = { ...updatedContent, datasources: results[1].items };
          }
        });
        //if anything has changed, update the react state
        if(updatedContent){
          yield put(updateContentDetail(updatedContent));
        }
        break;
      case 'view':
        if (!Views.isViewDetailInfo(action.content)) {
          const viewDetailInfo = yield getViewDetailInfo(action.content);
          yield put(updateContentDetail(viewDetailInfo));
        }
        break;
      case 'datasource':
        const ds = action.content as any;
        if (!ds.connections) {
          const connections = yield getDataConnections(action.content);
          let project = null;
          if(connections.length > 0 && connections[0].datasource) {
            project = connections[0].datasource.project;
          }
          const updatedContent = project ? { ...action.content, connections, project } : { ...action.content, connections};
          yield put(updateContentDetail(updatedContent));
        }
        break;
      case 'dataRole':
        if(!DataRoles.isDataRoleDetailedInfo(action.content)) {
          const dataRoleInfo = yield getDataRoleDetailInfo(action.content);
          yield put(updateContentDetail(dataRoleInfo));
        }
        break;
      case 'metric':
        if (!Metrics.isMetricInfo(action.content)) {
          const metricInfo = yield getMetricInfo(action.content);
          yield put(updateContentDetail(metricInfo));
        }
        break;
      case 'flow':
        if(!Flows.isFlowInfo(action.content) &&
          (action.content as IContentInfo<'flow'>).draftStatus !== ContentDraftStatus.unpublished) {
          const flowInfo = yield getFlowInfo(action.content);
          yield put(updateContentDetail(flowInfo));
        }
        break;
      case 'publishedConnection':
        if (!isPublishedConnectionInfo(action.content)) {
          const publishedConnectionInfo = yield getPublishedConnectionDetailInfo(action.content);
          yield put(updateContentDetail(publishedConnectionInfo));
        }
        break;
    }
  }
  catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getViewDetailInfo(content: IExplorableItemTypeInfo): IPromise<IViewDetailInfo> {
  return Views.getById(content.id);
}

export function getDataRoleDetailInfo(content: IExplorableItemTypeInfo): IPromise<IDataRoleDetailInfo> {
  return DataRoles.getById(content.id);
}

export function getFlowInfo(content: IExplorableItemTypeInfo): IPromise<FlowBasicInfo> {
  return Flows.getById(content.id);
}

export function getPublishedConnectionDetailInfo(content: IExplorableItemTypeInfo): IPromise<IPublishedConnectionDetailInfo> {
  return PublishedConnections.getById(content.id);
}

export function getMetricInfo(content: IExplorableItemTypeInfo): IPromise<IMetricInfo> {
  return Metrics.getById(content.id);
}

function getWorkbookInfo(content: IExplorableItemTypeInfo): IPromise<IWorkbookInfo> {
  return Workbooks.all().addFilter(Filter.stringFieldEq('id', content.id)).getSlice(0, 1).then(result => result.items[0]);
}

function getDataConnections(content: IExplorableItemTypeInfo): Promise<IDataConnectionInfo[]> {
  return DataSources.fromContent(content).then(datasourceInfos => {
    if (datasourceInfos.length > 0) {
      const connections = ServiceLocator.get<DataConnections>('DataConnections');
      return connections.getFromDatasource(datasourceInfos[0]);
    } else {
      return [];
    }
  });
}

function getDatasourcesForWorkbook(workbookId: string): Promise<ListResult<IDataSourceInfo>> {
  return DataSources.getByWorkbookId(workbookId);
}
